import { type FC, useId, useState } from 'react';
import { Collapse, Group, Menu, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { useLocation, useNavigate } from 'react-router';
import classNames from 'classnames';
import { useBreakpoint } from '~/shared/lib/hooks/useBreakpoint';
import { type NavbarSubTab, type NavbarTab } from '../../types';
import styles from '../Tab/Tab.module.css';

interface TabMenuProps {
    tab: NavbarTab;
    onClose?: VoidFunction;
    compact?: boolean;
}

const isSubTabActive = (tab: NavbarSubTab, pathname: string) => {
    return tab.isActive?.(pathname);
};

const getSubTabAriaLabel = (parentLabel: string, childLabel: string) => {
    return `${parentLabel}: ${childLabel}`;
};

export const TabMenu: FC<TabMenuProps> = ({ tab, onClose, compact }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const isMobile = useBreakpoint('mobile');

    const { icon: Icon, label, children = [], isActive } = tab;
    const active = isActive?.(location.pathname) || children.some((child) => isSubTabActive(child, location.pathname));
    const [opened, setOpened] = useState(false);
    const mobileDropdownId = useId();

    const linkClasses = classNames(styles.link, {
        [styles.compact]: compact,
    });

    const handleSubTabClick = async (subTab: NavbarSubTab) => {
        await navigate(subTab.to);
        onClose?.();
    };

    const handleParentClick = async () => {
        const firstChild = children[0];

        if (firstChild) {
            await navigate(firstChild.to);
            onClose?.();
        }
    };

    const handleMobileParentClick = () => {
        setOpened((current) => !current);
    };

    const renderParentContent = () => {
        if (compact) {
            return (
                <Icon
                    size={20}
                    stroke={1.5}
                />
            );
        }

        const ChevronIcon = isMobile && opened ? IconChevronDown : IconChevronRight;

        return (
            <Group
                gap='xs'
                wrap='nowrap'
                w='100%'
            >
                <Icon stroke={1.5} />

                <Text
                    size='md'
                    flex={1}
                >
                    {label}
                </Text>

                <ChevronIcon
                    size={16}
                    stroke={1.5}
                />
            </Group>
        );
    };

    if (isMobile) {
        return (
            <Stack
                gap={4}
                w='100%'
            >
                <UnstyledButton
                    w='100%'
                    p='sm'
                    className={linkClasses}
                    aria-label={label}
                    aria-expanded={opened}
                    aria-controls={mobileDropdownId}
                    onClick={handleMobileParentClick}
                >
                    {renderParentContent()}
                </UnstyledButton>

                <Collapse
                    in={opened}
                    id={mobileDropdownId}
                >
                    <Stack
                        gap={4}
                        pl='md'
                    >
                        {children.map((child) => {
                            const ChildIcon = child.icon;

                            return (
                                <UnstyledButton
                                    key={child.to}
                                    w='100%'
                                    p='xs'
                                    pl='lg'
                                    className={linkClasses}
                                    onClick={() => handleSubTabClick(child)}
                                    data-active={isSubTabActive(child, location.pathname) || undefined}
                                    aria-label={getSubTabAriaLabel(label, child.label)}
                                >
                                    <Group
                                        gap='xs'
                                        wrap='nowrap'
                                    >
                                        {ChildIcon && (
                                            <ChildIcon
                                                size={18}
                                                stroke={1.5}
                                            />
                                        )}

                                        <Text size='sm'>{child.label}</Text>
                                    </Group>
                                </UnstyledButton>
                            );
                        })}
                    </Stack>
                </Collapse>
            </Stack>
        );
    }

    return (
        <Menu
            width={200}
            position='right-start'
            trigger='hover'
            openDelay={120}
            closeDelay={150}
            withinPortal
        >
            <Menu.Target>
                <UnstyledButton
                    w='100%'
                    p='sm'
                    className={linkClasses}
                    data-active={active || undefined}
                    aria-label={label}
                    onClick={handleParentClick}
                >
                    {renderParentContent()}
                </UnstyledButton>
            </Menu.Target>

            <Menu.Dropdown>
                <Menu.Label>История</Menu.Label>

                <Stack gap={4}>
                    {children.map((child) => {
                        const ChildIcon = child.icon;
                        const subTabActive = isSubTabActive(child, location.pathname);

                        return (
                            <Menu.Item
                                key={child.to}
                                className={styles.menuItem}
                                leftSection={
                                    ChildIcon ? (
                                        <ChildIcon
                                            size={16}
                                            stroke={1.5}
                                        />
                                    ) : undefined
                                }
                                onClick={() => handleSubTabClick(child)}
                                data-active={subTabActive || undefined}
                            >
                                {child.label}
                            </Menu.Item>
                        );
                    })}
                </Stack>
            </Menu.Dropdown>
        </Menu>
    );
};
